import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Loader2, ShieldAlert, Trophy, Lightbulb, CheckCircle2, AlertTriangle, ArrowRight, RotateCcw, Flag } from "lucide-react";
import { toast } from "sonner";

export default function AMLSimulator() {
  const { data, isLoading } = useQuery({ queryKey: ["aml-cases"], queryFn: async () => (await api.get("/aml-sim/cases")).data });
  const { data: histData, refetch } = useQuery({ queryKey: ["aml-results"], queryFn: async () => (await api.get("/aml-sim/results")).data });
  const [active, setActive] = useState(null);

  if (isLoading) return <div className="flex justify-center py-24"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;
  if (active) return <CaseView caseData={active} onDone={refetch} onExit={() => setActive(null)} />;

  const cases = data?.cases || [];
  const results = histData?.results || [];

  return (
    <div className="space-y-6" data-testid="aml-sim-page">
      <div>
        <p className="text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground mb-1">Financial Investigation Unit · Revolut-style</p>
        <h1 className="font-heading text-3xl sm:text-4xl font-black tracking-tighter">AML Case Simulator</h1>
        <p className="text-muted-foreground mt-1">Investigate a transaction alert, decide to escalate or close, and write a SAR. AI scores your judgement.</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {cases.map((c) => (
          <div key={c.id} className="p-6 rounded-md border border-border bg-card flex flex-col hover:-translate-y-1 transition-transform" data-testid={`aml-case-${c.id}`}>
            <div className="flex items-center justify-between mb-4">
              <div className="w-10 h-10 rounded-md bg-primary/10 flex items-center justify-center"><ShieldAlert className="w-5 h-5 text-primary" /></div>
              <span className="text-xs font-mono px-2 py-0.5 rounded bg-chart-4/15 text-chart-4">{c.difficulty}</span>
            </div>
            <span className="text-xs font-mono uppercase tracking-wider text-muted-foreground mb-1">{c.alert_type}</span>
            <h3 className="font-heading font-bold text-lg mb-2">{c.title}</h3>
            <p className="text-sm text-muted-foreground mb-6 flex-1">{c.transactions?.length} flagged transactions to review.</p>
            <Button className="rounded-md gap-2" onClick={() => setActive(c)} data-testid={`open-case-${c.id}`}>
              Open Case <ArrowRight className="w-4 h-4" />
            </Button>
          </div>
        ))}
      </div>

      {results.length > 0 && (
        <div className="p-6 rounded-md border border-border bg-card">
          <h3 className="font-heading font-bold text-lg mb-4">Your Recent Investigations</h3>
          <div className="space-y-2" data-testid="aml-history">
            {results.slice(0, 8).map((r) => (
              <div key={r.id} className="flex items-center justify-between py-2 border-b border-border last:border-0">
                <div className="min-w-0">
                  <p className="text-sm font-medium truncate">{r.case_title}</p>
                  <p className="text-xs text-muted-foreground font-mono">{r.decision_correct ? "Correct call" : "Wrong call"} · SAR {r.sar_quality}</p>
                </div>
                <span className={`font-mono font-bold ${r.overall >= 75 ? "text-success" : r.overall >= 50 ? "text-chart-4" : "text-destructive"}`}>{r.overall}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function CaseView({ caseData, onDone, onExit }) {
  const [decision, setDecision] = useState(null);
  const [narrative, setNarrative] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  const submit = async () => {
    if (!decision) { toast.error("Choose a triage decision."); return; }
    if (narrative.trim().length < 20) { toast.error("Write a fuller SAR narrative."); return; }
    setSubmitting(true);
    try {
      const { data } = await api.post("/aml-sim/submit", { case_id: caseData.id, decision, sar_narrative: narrative });
      setResult(data.result);
      onDone();
      toast.success("Case evaluated!");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Evaluation failed.");
    } finally { setSubmitting(false); }
  };

  const reset = () => { setDecision(null); setNarrative(""); setResult(null); };

  if (result) {
    return (
      <div className="max-w-2xl mx-auto" data-testid="aml-result">
        <button onClick={onExit} className="text-sm text-muted-foreground hover:text-foreground mb-6">← Back to cases</button>
        <div className="text-center mb-8">
          <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-3"><Trophy className="w-8 h-8 text-primary" /></div>
          <h2 className="font-heading text-4xl font-black">{result.overall}<span className="text-lg text-muted-foreground">/100</span></h2>
          <p className={`text-sm font-mono mt-1 ${result.decision_correct ? "text-success" : "text-destructive"}`}>
            {result.decision_correct ? "✓ Correct triage decision" : `✗ Correct outcome was to ${result.expected_decision.toUpperCase()}`}
          </p>
        </div>
        <div className="grid grid-cols-3 gap-3 mb-6">
          <ScoreBox label="Decision" value={result.decision_accuracy} testid="aml-score-decision" />
          <ScoreBox label="Red Flags" value={result.red_flag_identification} testid="aml-score-flags" />
          <ScoreBox label="SAR Quality" value={result.sar_quality} testid="aml-score-sar" />
        </div>
        <div className="p-5 rounded-md border border-border bg-card mb-4">
          <p className="text-sm font-semibold mb-2">AI Feedback</p>
          <p className="text-sm text-muted-foreground leading-relaxed">{result.feedback}</p>
        </div>
        {result.identified_flags?.length > 0 && (
          <div className="p-5 rounded-md border border-border bg-card mb-4">
            <p className="text-sm font-semibold mb-3 flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-success" /> Flags You Caught</p>
            <ul className="space-y-2">{result.identified_flags.map((t, i) => <li key={i} className="text-sm text-muted-foreground flex gap-2"><span className="text-success">•</span>{t}</li>)}</ul>
          </div>
        )}
        {result.missed_flags?.length > 0 && (
          <div className="p-5 rounded-md border border-destructive/30 bg-destructive/5 mb-4">
            <p className="text-sm font-semibold mb-3 flex items-center gap-2"><Flag className="w-4 h-4 text-destructive" /> Flags You Missed</p>
            <ul className="space-y-2">{result.missed_flags.map((t, i) => <li key={i} className="text-sm text-muted-foreground flex gap-2"><span className="text-destructive">•</span>{t}</li>)}</ul>
          </div>
        )}
        {result.improvements?.length > 0 && (
          <div className="p-5 rounded-md border border-border bg-card mb-6">
            <p className="text-sm font-semibold mb-3 flex items-center gap-2"><Lightbulb className="w-4 h-4 text-primary" /> Improve</p>
            <ul className="space-y-2">{result.improvements.map((t, i) => <li key={i} className="text-sm text-muted-foreground flex gap-2"><span className="text-primary">•</span>{t}</li>)}</ul>
          </div>
        )}
        <div className="flex gap-3">
          <Button variant="outline" className="rounded-md gap-2" onClick={reset} data-testid="aml-retry"><RotateCcw className="w-4 h-4" /> Retry Case</Button>
          <Button className="rounded-md" onClick={onExit} data-testid="aml-done">Done</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto" data-testid="aml-case-view">
      <button onClick={onExit} className="text-sm text-muted-foreground hover:text-foreground mb-6">← Back to cases</button>
      <div className="flex items-center gap-2 mb-2">
        <ShieldAlert className="w-5 h-5 text-primary" />
        <span className="text-xs font-mono uppercase tracking-wider text-muted-foreground">{caseData.alert_type}</span>
      </div>
      <h1 className="font-heading text-2xl sm:text-3xl font-black tracking-tight mb-6">{caseData.title}</h1>

      {/* Customer */}
      <div className="p-5 rounded-md border border-border bg-card mb-4" data-testid="aml-customer">
        <p className="text-xs font-mono uppercase tracking-wider text-muted-foreground mb-2">Customer Profile</p>
        <div className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm">
          <div><span className="text-muted-foreground">Name:</span> <span className="font-medium">{caseData.customer?.name}</span></div>
          <div><span className="text-muted-foreground">Account age:</span> <span className="font-medium">{caseData.customer?.account_age}</span></div>
          <div><span className="text-muted-foreground">Occupation:</span> <span className="font-medium">{caseData.customer?.occupation}</span></div>
          <div className="col-span-2"><span className="text-muted-foreground">Expected activity:</span> <span className="font-medium">{caseData.customer?.expected_activity}</span></div>
        </div>
      </div>

      {/* Transactions */}
      <div className="p-5 rounded-md border border-border bg-card mb-4">
        <p className="text-xs font-mono uppercase tracking-wider text-muted-foreground mb-3">Flagged Transactions</p>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="aml-transactions">
            <thead><tr className="text-left text-muted-foreground font-mono text-xs uppercase border-b border-border">
              <th className="py-2 pr-3">Date</th><th className="py-2 pr-3">Type</th><th className="py-2 pr-3">Amount</th><th className="py-2 pr-3">Counterparty</th><th className="py-2">Note</th>
            </tr></thead>
            <tbody>
              {caseData.transactions?.map((t, i) => (
                <tr key={i} className="border-b border-border last:border-0">
                  <td className="py-2 pr-3 font-mono text-xs">{t.date}</td>
                  <td className="py-2 pr-3">{t.type}</td>
                  <td className="py-2 pr-3 font-semibold">{t.amount}</td>
                  <td className="py-2 pr-3 text-muted-foreground">{t.counterparty}</td>
                  <td className="py-2 text-muted-foreground text-xs">{t.note}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="p-5 rounded-md border border-chart-4/30 bg-chart-4/5 mb-6 flex items-start gap-2" data-testid="aml-context">
        <AlertTriangle className="w-4 h-4 text-chart-4 shrink-0 mt-0.5" />
        <p className="text-sm">{caseData.context}</p>
      </div>

      {/* Decision */}
      <p className="text-sm font-semibold mb-3">1. Your triage decision</p>
      <div className="grid grid-cols-2 gap-3 mb-6">
        <button onClick={() => setDecision("escalate")} data-testid="decision-escalate"
          className={`p-4 rounded-md border text-left transition-colors ${decision === "escalate" ? "border-primary bg-primary/5" : "border-border bg-card hover:bg-secondary"}`}>
          <p className="font-semibold">Escalate & file SAR</p>
          <p className="text-xs text-muted-foreground mt-1">Suspicious — report to the FIU.</p>
        </button>
        <button onClick={() => setDecision("close")} data-testid="decision-close"
          className={`p-4 rounded-md border text-left transition-colors ${decision === "close" ? "border-primary bg-primary/5" : "border-border bg-card hover:bg-secondary"}`}>
          <p className="font-semibold">Close as false positive</p>
          <p className="text-xs text-muted-foreground mt-1">Legitimate — no report needed.</p>
        </button>
      </div>

      {/* SAR */}
      <p className="text-sm font-semibold mb-3">2. Write your SAR narrative</p>
      <Textarea value={narrative} onChange={(e) => setNarrative(e.target.value)} data-testid="sar-narrative"
        placeholder="Describe who, what, when, where and why this is suspicious (or why it's legitimate). Include amounts and the red flags you identified..."
        className="rounded-md min-h-[160px] mb-2" />
      <p className="text-xs text-muted-foreground mb-6 font-mono">{narrative.trim().length} chars</p>

      <Button onClick={submit} disabled={submitting} className="rounded-md gap-2 h-11 w-full sm:w-auto" data-testid="submit-case">
        {submitting ? <><Loader2 className="w-4 h-4 animate-spin" /> Evaluating...</> : <><ShieldAlert className="w-4 h-4" /> Submit Investigation</>}
      </Button>
      {submitting && <div className="h-1 rounded-full tracing-beam mt-4" />}
    </div>
  );
}

function ScoreBox({ label, value, testid }) {
  return (
    <div className="p-4 rounded-md border border-border bg-card text-center" data-testid={testid}>
      <p className="font-mono text-2xl font-bold">{value}</p>
      <p className="text-xs text-muted-foreground mt-1">{label}</p>
    </div>
  );
}
